import React, { useState, useEffect } from 'react';
import SubcontractorEmployeeForm from './SubcontractorEmployeeForm';
import {
  updateSubcontractorEmployee,
  getSubcontractorEmployees,
  bulkUploadSubcontractorEmployeeDocuments,
  deleteSubcontractorEmployeeDocument,
  imagepath
} from '../api';
import { message, Spin } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';

const SubcontractorEmployeeEdit = () => {
  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const navigate = useNavigate();
  const { subcontractorId, employeeId } = useParams();

  const safeDate = (dateStr) => {
    if (!dateStr || dateStr.startsWith('0000-00-00')) return '';
    try {
      const d = new Date(dateStr);
      return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
    } catch (e) {
      return '';
    }
  };

  useEffect(() => {
    // We need to fetch the specific employee data for editing
    // Assuming getSubcontractorEmployees can fetch a single employee
    // In a real app, you might have a getSubcontractorEmployee(id) function
    getSubcontractorEmployees(subcontractorId).then(res => {
      if (res.success) {
        const emp = res.data.find(e => e.id === parseInt(employeeId, 10));
        if (emp) {
          if (emp.profile_image) {
            emp.profile_image = [{
              uid: '-1',
              name: emp.profile_image.split('/').pop(),
              status: 'done',
              url: `${imagepath}${emp.profile_image}`,
            }];
          } else {
            emp.profile_image = [];
          }
          emp.dob = safeDate(emp.dob);
          emp.joining_date = safeDate(emp.joining_date);
          setInitialData(emp);
        }
      }
    });
  }, [subcontractorId, employeeId]);


  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // 1. Handle document deletions
      const removedDocIds = (initialData.documents || []).filter(initDoc => {
        const currentDoc = values.documents?.find(d => d.id === initDoc.id);
        if (!currentDoc) return true;
        const hasNewFile = currentDoc.document_file?.[0]?.originFileObj;
        const hasNoFile = !currentDoc.document_file || currentDoc.document_file.length === 0;
        return !!(hasNewFile || hasNoFile);
      }).map(doc => doc.id);

      if (removedDocIds.length > 0) {
        await Promise.all(
          removedDocIds.map(docId => deleteSubcontractorEmployeeDocument(employeeId, docId))
        );
      }

      // 2. Update employee details
      const formData = new FormData();
      formData.append('_method', 'PUT');
      formData.append('subcontractor_id', subcontractorId);

      Object.entries(values).forEach(([key, value]) => {
        if (key === 'documents') return;
        if (key === 'profile_image') {
          if (value && value[0] && value[0].originFileObj) {
            formData.append('profile_image', value[0].originFileObj);
          }
          return;
        }
        if (value !== undefined && value !== null && value !== '') {
          formData.append(key, value);
        }
      });

      const res = await updateSubcontractorEmployee(employeeId, formData);
      if (!res.success) throw new Error(res.message || 'Failed to update employee');

      // 3. Handle document uploads
      if (values.documents && values.documents.length > 0) {
        const docsToUpload = values.documents
          .filter(doc => doc.document_file && doc.document_file.length > 0 && doc.document_file[0].originFileObj)
          .map(doc => ({
            document_type: doc.document_type,
            issue_date: doc.issue_date,
            expiry_date: doc.expiry_date,
            document_file: doc.document_file[0].originFileObj
          }));

        if (docsToUpload.length > 0) {
          const docRes = await bulkUploadSubcontractorEmployeeDocuments(employeeId, docsToUpload);
          if (!docRes.success) throw new Error(docRes.message || 'Failed to upload documents');
        }
      }

      message.success('Employee updated');
      navigate(`/subcontractors`);
    } catch (err) {
      console.error('Update error:', err);
      message.error(err.message || 'Failed to update employee');
    } finally {
      setLoading(false);
    }
  };

  return initialData ? (
    <SubcontractorEmployeeForm
      mode="edit"
      initialValues={initialData}
      onSubmit={handleSubmit}
      loading={loading}
      onCancel={() => navigate(`/subcontractors`)}
    />
  ) : (
    <div>Loading...</div>
  );
};

export default SubcontractorEmployeeEdit; 